/* eslint-disable camelcase */
import Reflux from 'reflux';
import makeApiRequest from '../utils/makeApiRequest';
import { SCOPES, SETTINGS_KEYS } from '../configs/viewSettings';
import { makeKey, prepareFieldId } from '../utils/viewSettings';
import createAsyncAction from '../helpers/createAsyncAction';

import appState from '../appState';
import { prepareViewSettingsRequest } from '../helpers/prepareViewSettingsRequest';

const isWebForm = () => !!appState.getIn(['ui', 'isWebForm']);
const isVirtualView = (viewId) => !viewId || viewId === '$new';

const actions = {
  loadViewSettings: createAsyncAction(function ({ catalogId, viewId, merged = true, targetViewId }) {
    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId }, { merged });

    return makeApiRequest(url, {
      method: 'get',
      query,
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, viewId: targetViewId || viewId, merged });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  loadSettingsByScope: createAsyncAction(function ({ catalogId, viewId, scope }) {
    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});

    return makeApiRequest(url, {
      method: 'get',
      query,
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setFieldWidth: createAsyncAction(function ({ catalogId, viewMode, fieldId, width, scope = SCOPES.PERSONAL, viewId }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});
    const apiFieldId = prepareFieldId(fieldId);

    const apiKey = makeKey(viewMode, SETTINGS_KEYS.WIDTH, apiFieldId);
    const settings = { [apiKey]: width };

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setFieldPinned: createAsyncAction(function ({
    catalogId,
    viewMode,
    fieldId,
    pinned,
    scope = SCOPES.PERSONAL,
    viewId,
  }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});
    const apiFieldId = prepareFieldId(fieldId);

    const apiKey = makeKey(viewMode, SETTINGS_KEYS.PINNED, apiFieldId);
    const settings = { [apiKey]: pinned };

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setFieldVisibility: createAsyncAction(function ({
    catalogId,
    viewMode,
    fieldId,
    visible,
    scope = SCOPES.PERSONAL,
    viewId,
  }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});
    const apiFieldId = prepareFieldId(fieldId);

    const apiKey = makeKey(viewMode, SETTINGS_KEYS.VISIBLE, apiFieldId);
    const settings = { [apiKey]: visible };

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setAllFieldsVisibility: createAsyncAction(function ({
    catalogId,
    viewMode,
    fieldIds,
    visible,
    scope = SCOPES.PERSONAL,
    viewId,
  }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});

    const settings = {};
    fieldIds.forEach((fieldId) => {
      const apiKey = makeKey(viewMode, SETTINGS_KEYS.VISIBLE, prepareFieldId(fieldId));
      settings[apiKey] = visible;
    });

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setFieldsOrder: createAsyncAction(function ({ catalogId, viewMode, fieldsOrder, scope = SCOPES.PERSONAL, viewId }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});

    const apiKey = makeKey(viewMode, SETTINGS_KEYS.FIELDS_ORDER);
    const settings = { [apiKey]: { fieldIds: fieldsOrder } };

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setFieldsGrouping: createAsyncAction(function ({ catalogId, viewMode, groups, scope = SCOPES.PERSONAL, viewId }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});

    const apiKey = makeKey(viewMode, SETTINGS_KEYS.GROUPS);
    const settings = { [apiKey]: { fieldIds: groups } };

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setRowHeight: createAsyncAction(function ({ catalogId, viewMode, rowHeight, scope = SCOPES.PERSONAL, viewId }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});

    const apiKey = makeKey(viewMode, SETTINGS_KEYS.ROW_HEIGHT);
    const settings = { [apiKey]: rowHeight };

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setSortingRecords: createAsyncAction(function ({
    catalogId,
    viewMode,
    sortField,
    sortType,
    scope = SCOPES.PERSONAL,
    viewId,
  }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});

    const apiKey = makeKey(viewMode, SETTINGS_KEYS.SORTING_RECORDS);
    const sortingData = { sortType, fieldId: prepareFieldId(sortField) };
    const settings = { [apiKey]: sortingData };

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  setOption: createAsyncAction(function ({ catalogId, viewId, viewMode, option, value, scope = SCOPES.PERSONAL }) {
    if (isWebForm() || isVirtualView(viewId)) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});

    const apiKey = makeKey(viewMode, option);
    const settings = { [apiKey]: Array.isArray(value) ? { fieldIds: value } : value };

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  batchUpdateSettings: createAsyncAction(function ({ catalogId, viewId, scope = SCOPES.GLOBAL, settings }) {
    if (isWebForm()) return;

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId }, {});

    return makeApiRequest(url, {
      method: 'post',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  patchBatchSettings: createAsyncAction(function ({ catalogId, viewId, scope = SCOPES.PERSONAL, settings }) {
    if (isWebForm()) return;

    if (!settings || Object.keys(settings).length === 0) {
      this.completed({ scope, settings: {} }, { catalogId, scope, viewId });
      return Promise.resolve();
    }

    const { url, query } = prepareViewSettingsRequest({ catalogId, viewId, scope }, {});

    return makeApiRequest(url, {
      method: 'patch',
      query,
      body: { scope, settings },
    }).then(
      (res) => {
        this.completed(res.body, { catalogId, scope, viewId });
      },
      (err) => {
        this.failed(err);
      },
    );
  }),

  // Синхронные actions для обновления AppState
  setViewSettingsToAppState: Reflux.createAction({ sync: false }),
  mergeViewSettingsToAppState: Reflux.createAction({ sync: false }),
  initViewSettings: Reflux.createAction({ sync: false }),
  setViewSettingsClipboard: Reflux.createAction({ sync: false }),
  clearViewSettingsClipboard: Reflux.createAction({ sync: false }),
};

export default actions;
